import React, { useEffect, useState } from 'react';
import { Card, Button, Form, Alert, Spinner, Table, Badge, Row, Col } from 'react-bootstrap';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import qaReportService from '../services/qaReportService';
import DailyReportLog from './DailyReportLog';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';

const buildCsvRows = (report, visibility = {}) => {
  const rows = [];
  const isVisible = (section) => visibility[section] !== false;

  rows.push(['Section', 'Item', 'Value']);
  rows.push(['Report', 'Type', report.title]);
  rows.push(['Report', 'Range Start', report.rangeStart]);
  rows.push(['Report', 'Range End', report.rangeEnd]);
  if (report.notes) {
    rows.push(['Report', 'Notes', report.notes]);
  }

  if (isVisible('keyMetrics')) {
    rows.push(['Learners', 'Total', report.learners.total]);
    rows.push(['Learners', 'Verified', report.learners.verified]);
    rows.push(['Learners', 'Unverified', report.learners.unverified]);

    rows.push(['Attendance', 'Total Records', report.attendance.total]);
    rows.push(['Attendance', 'Present', report.attendance.present]);
    rows.push(['Attendance', 'Absent', report.attendance.absent]);
    rows.push(['Attendance', 'Excused', report.attendance.excused]);
    rows.push(['Attendance', 'Incomplete', report.attendance.incomplete]);

    rows.push(['Attendance', 'Signed', report.signedAttendance.signed]);
    rows.push(['Attendance', 'Attended Not Signed', report.signedAttendance.attendedNotSigned]);

    rows.push(['Drop-offs', 'Total', report.dropOffs.total]);
  }

  if (isVisible('announcements')) {
    rows.push(['Announcements', 'Total', report.announcements.total]);
    report.announcements.items.forEach((item) => {
      rows.push(['Announcements', item.subject, `${item.recipientCount} recipients (${item.providers.join(', ')})`]);
    });
  }

  if (isVisible('sessions')) {
    rows.push(['Sessions', 'Total', report.sessions.total]);
    report.sessions.items.forEach((item) => {
      rows.push([
        'Sessions',
        `${item.provider} ${item.moduleName}`,
        `${item.sessionDate} ${item.sessionTime} (${item.timeSlot}) - ${item.attendance.present}/${item.attendance.total} present${item.notes ? ` | Notes: ${item.notes}` : ''}`
      ]);
    });
  }

  if (isVisible('attendanceByLearner') && report.attendanceByLearner) {
    report.attendanceByLearner.forEach((item) => {
      rows.push([
        'Attendance By Learner',
        `${item.name} (${item.cohortName || 'Unknown'})`,
        `${item.present}/${item.total} (${item.attendanceRate}%)`
      ]);
    });
  }

  if (isVisible('communications')) {
    rows.push(['Issues', 'Total', report.issues.total]);
    rows.push(['Issues', 'Technical', report.issues.byCategory.technical]);
    rows.push(['Issues', 'Classroom', report.issues.byCategory.classroom]);
    report.issues.resolved.forEach((item) => {
      rows.push(['Issues Resolved', item.title, item.status]);
    });
  }

  if (isVisible('teamsMismatch')) {
    rows.push(['Teams Mismatch', 'Total', report.teamsMismatch.total]);
  }

  if (isVisible('weeklyDocuments') && report.weeklyDocuments) {
    rows.push(['Weekly Documents', 'Submitted', report.weeklyDocuments.submitted]);
    rows.push(['Weekly Documents', 'Outstanding', report.weeklyDocuments.outstanding]);
  }

  if (isVisible('facilitatorReports') && report.facilitatorReports) {
    rows.push(['Facilitator Reports', 'Total', report.facilitatorReports.total]);
    if (report.facilitatorReports.items?.length) {
      report.facilitatorReports.items.forEach((item) => {
        const range = item.type === 'weekly_highs_lows'
          ? `${item.weekStart || ''} - ${item.weekEnd || ''}`
          : (item.date || '');
        const detail = item.type === 'weekly_highs_lows'
          ? `Highs: ${item.weeklyHighs || ''} | Lows: ${item.weeklyLows || ''}`
          : `Comment: ${item.dailyComment || ''}`;
        rows.push(['Facilitator Notes', item.cohortName, `${item.type} ${range}`, detail]);
      });
    }
  }

  return rows;
};

const downloadCsv = (rows, filename) => {
  const csvContent = rows
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

const loadLogo = () => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = reject;
  image.src = `${process.env.PUBLIC_URL || ''}/NPI_Logo.png`;
});

const addLogo = (doc, image) => {
  const targetWidth = 32;
  const ratio = image.naturalHeight / image.naturalWidth;
  const targetHeight = targetWidth * ratio;
  doc.addImage(image, 'PNG', 14, 10, targetWidth, targetHeight);
  return targetHeight;
};

const renderWeeklyTemplate = (doc, report) => {
  const defaults = {
    weekNumber: '',
    weekEnding: '',
    currentPhase: '',
    submittedBy: '',
    weeklySummary: 'Provide a brief 3-4 sentence overview of this week\'s key activities and outcomes:',
    activities: new Array(6).fill(null).map(() => ({ activity: '', plannedActual: '', status: '' })),
    attendanceMetrics: [
      { metric: 'Active learners (attending)', thisWeek: '', lastWeek: '', trend: '' },
      { metric: 'Average daily attendance rate (%)', thisWeek: '', lastWeek: '', trend: '' },
      { metric: 'Learners with perfect attendance', thisWeek: '', lastWeek: '', trend: '' },
      { metric: 'Learners with attendance concerns (<75%)', thisWeek: '', lastWeek: '', trend: '' },
      { metric: 'New dropouts this week', thisWeek: '', lastWeek: '', trend: '' },
      { metric: 'Total cumulative dropouts', thisWeek: '', lastWeek: '', trend: '' }
    ],
    attendanceNotes: 'Explain any attendance concerns, dropout reasons, or interventions implemented:',
    assessments: [
      { activity: 'Knowledge questionnaires administered', completed: '', comments: '' },
      { activity: 'Workplace assignments completed', completed: '', comments: '' },
      { activity: 'Practical observations conducted', completed: '', comments: '' },
      { activity: 'POE submissions reviewed/signed off', completed: '', comments: '' }
    ],
    challenges: [
      'Issue 1: [Description and action taken]',
      'Issue 2: [Description and action taken]',
      'Issue 3: [Description and action taken]'
    ],
    escalations: 'Any issues requiring Naspers Labs/Takealot attention or support:',
    nextWeekPlan: [
      'Activity/Priority 1',
      'Activity/Priority 2',
      'Activity/Priority 3',
      'Activity/Priority 4'
    ],
    quickWins: [
      'Share 2-3 positive moments, achievements, or learner successes from this week:',
      '',
      ''
    ]
  };

  const template = {
    ...defaults,
    ...report.weeklyTemplate,
    activities: report.weeklyTemplate?.activities?.length ? report.weeklyTemplate.activities : defaults.activities,
    attendanceMetrics: report.weeklyTemplate?.attendanceMetrics?.length ? report.weeklyTemplate.attendanceMetrics : defaults.attendanceMetrics,
    assessments: report.weeklyTemplate?.assessments?.length ? report.weeklyTemplate.assessments : defaults.assessments,
    challenges: report.weeklyTemplate?.challenges?.length ? report.weeklyTemplate.challenges : defaults.challenges,
    nextWeekPlan: report.weeklyTemplate?.nextWeekPlan?.length ? report.weeklyTemplate.nextWeekPlan : defaults.nextWeekPlan,
    quickWins: report.weeklyTemplate?.quickWins?.length ? report.weeklyTemplate.quickWins : defaults.quickWins
  };

  let yPos = 16;
  doc.setTextColor(31, 78, 120);
  doc.setFontSize(14);
  doc.text('PERSONAL SHOPPER TRAINING PROGRAMME', 105, yPos, { align: 'center' });
  yPos += 8;
  doc.setTextColor(46, 117, 181);
  doc.setFontSize(16);
  doc.text('Weekly Progress Update', 105, yPos, { align: 'center' });
  yPos += 10;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(11);

  autoTable(doc, {
    startY: yPos,
    head: [['Week #', 'Week Ending', 'Current Phase', 'Submitted By']],
    body: [[
      template.weekNumber || '',
      template.weekEnding || '',
      template.currentPhase || '',
      template.submittedBy || ''
    ]],
    styles: { fontSize: 9 },
    theme: 'grid'
  });
  yPos = doc.lastAutoTable.finalY + 8;

  doc.setFontSize(12);
  doc.text('1. WEEKLY SUMMARY', 14, yPos);
  yPos += 6;
  doc.setFontSize(10);
  const summary = template.weeklySummary || defaults.weeklySummary;
  doc.text(summary || ' ', 14, yPos, { maxWidth: 180 });
  yPos += 12;

  doc.setFontSize(12);
  doc.text("2. THIS WEEK'S ACTIVITIES", 14, yPos);
  yPos += 4;
  autoTable(doc, {
    startY: yPos + 2,
    head: [['Activity / Training Session', 'Planned / Actual', 'Status']],
    body: (template.activities || []).map((item) => [
      item.activity || '',
      item.plannedActual || '',
      item.status || ''
    ]),
    styles: { fontSize: 9 },
    theme: 'grid'
  });
  yPos = doc.lastAutoTable.finalY + 4;
  doc.setFontSize(9);
  doc.text('Status: ✓ Complete | ► In Progress | ⚠ Delayed', 14, yPos);
  yPos += 8;

  doc.setFontSize(12);
  doc.text('3. LEARNER ATTENDANCE & ENGAGEMENT', 14, yPos);
  yPos += 4;
  autoTable(doc, {
    startY: yPos + 2,
    head: [['Metric', 'This Week', 'Last Week', 'Trend']],
    body: (template.attendanceMetrics || []).map((item) => [
      item.metric || '',
      item.thisWeek || '',
      item.lastWeek || '',
      item.trend || ''
    ]),
    styles: { fontSize: 9 },
    theme: 'grid'
  });
  yPos = doc.lastAutoTable.finalY + 4;
  doc.setFontSize(9);
  doc.text('Trend: ↑ Improving | → Stable | ↓ Declining', 14, yPos);
  yPos += 6;
  doc.setFontSize(10);
  doc.text('Attendance Notes', 14, yPos);
  yPos += 5;
  doc.text(template.attendanceNotes || defaults.attendanceNotes, 14, yPos, { maxWidth: 180 });
  yPos += 12;

  doc.setFontSize(12);
  doc.text('4. ASSESSMENT PROGRESS', 14, yPos);
  yPos += 4;
  autoTable(doc, {
    startY: yPos + 2,
    head: [['Assessment Activity', 'Completed', 'Status / Comments']],
    body: (template.assessments || []).map((item) => [
      item.activity || '',
      item.completed || '',
      item.comments || ''
    ]),
    styles: { fontSize: 9 },
    theme: 'grid'
  });
  yPos = doc.lastAutoTable.finalY + 6;

  doc.setFontSize(12);
  doc.text('5. CHALLENGES & ISSUES', 14, yPos);
  yPos += 6;
  doc.setFontSize(10);
  (template.challenges || []).forEach((issue) => {
    doc.text(`• ${issue}`, 14, yPos);
    yPos += 5;
  });
  if (!template.challenges || template.challenges.length === 0) {
    doc.text('•', 14, yPos);
    yPos += 5;
  }
  doc.text('Escalations Required', 14, yPos);
  yPos += 5;
  doc.text(template.escalations || defaults.escalations, 14, yPos, { maxWidth: 180 });
  yPos += 10;

  doc.setFontSize(12);
  doc.text("6. NEXT WEEK'S PLAN", 14, yPos);
  yPos += 6;
  doc.setFontSize(10);
  (template.nextWeekPlan || []).forEach((item) => {
    doc.text(`• ${item}`, 14, yPos);
    yPos += 5;
  });
  if (!template.nextWeekPlan || template.nextWeekPlan.length === 0) {
    doc.text('•', 14, yPos);
    yPos += 5;
  }
  yPos += 4;

  doc.setFontSize(12);
  doc.text('7. QUICK WINS & HIGHLIGHTS', 14, yPos);
  yPos += 6;
  doc.setFontSize(10);
  (template.quickWins || []).forEach((item) => {
    doc.text(`• ${item}`, 14, yPos);
    yPos += 5;
  });

  yPos += 6;
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text('END OF WEEKLY UPDATE', 105, yPos, { align: 'center' });
  yPos += 6;
  doc.text('Comprehensive monthly report to be submitted at month-end', 105, yPos, { align: 'center' });
};

const exportReportPdf = async (report, filename, visibility = {}) => {
  if (report?.title?.toLowerCase?.().includes('weekly')) {
    const doc = new jsPDF();
    renderWeeklyTemplate(doc, report);
    doc.save(filename);
    return;
  }
  const doc = new jsPDF();
  let yPos = 16;
  const cohortLabel = report.filters?.cohorts?.length
    ? report.filters.cohorts.map((item) => item.name).join(', ')
    : 'All Cohorts';
  const primaryCategory = report.issues?.primaryCategory || 'none';
  const isVisible = (section) => visibility[section] !== false;

  try {
    const logo = await loadLogo();
    const logoHeight = addLogo(doc, logo);
    yPos = Math.max(30, 10 + logoHeight) + 6;
  } catch (error) {
    yPos = 20;
  }

  doc.setFontSize(16);
  doc.text(report.title, 52, 18);
  doc.setFontSize(10);
  doc.text(`Range: ${report.rangeStart} to ${report.rangeEnd}`, 52, 25);
  doc.text(`Cohorts: ${cohortLabel}`, 52, 30);
  doc.setFontSize(9);
  doc.text('Generated by QA Report Center', 52, 35);

  yPos += 8;

  if (report.notes) {
    doc.setFontSize(11);
    doc.text('Notes', 14, yPos);
    yPos += 5;
    doc.setFontSize(10);
    const noteLines = doc.splitTextToSize(report.notes, 180);
    doc.text(noteLines, 14, yPos);
    yPos += noteLines.length * 5 + 4;
  }

  if (isVisible('keyMetrics')) {
    doc.setFontSize(12);
    doc.text('Key Metrics', 14, yPos);
    autoTable(doc, {
      startY: yPos + 4,
      head: [['Metric', 'Value']],
      body: [
        ['Learners Verified', `${report.learners.verified}/${report.learners.total}`],
        ['Attendance Records', `${report.attendance.present} present / ${report.attendance.total} total`],
        ['Signed Attendance', String(report.signedAttendance.signed)],
        ['Announcements Sent', String(report.announcements.total)],
        ['Issues Logged', String(report.issues.total)],
        ['Teams Cohort Mismatches', String(report.teamsMismatch.total)]
      ],
      styles: { fontSize: 9 },
      theme: 'grid'
    });
    yPos = doc.lastAutoTable.finalY + 6;
  }

  if (isVisible('attendanceByLearner') && report.attendanceByLearner) {
    doc.setFontSize(12);
    doc.text('Attendance by Learner', 14, yPos);
    autoTable(doc, {
      startY: yPos + 4,
      head: [['Learner', 'Cohort', 'Present', 'Total', 'Rate']],
      body: report.attendanceByLearner.length
        ? report.attendanceByLearner.map((item) => ([
          item.name,
          item.cohortName || '',
          item.present,
          item.total,
          `${item.attendanceRate}%`
        ]))
        : [['No attendance records.', '', '', '', '']],
      styles: { fontSize: 8 },
      theme: 'grid'
    });
    yPos = doc.lastAutoTable.finalY + 6;
  }

  if (isVisible('learnerSummary')) {
    doc.setFontSize(12);
    doc.text('Learner Verification Summary', 14, yPos);
    autoTable(doc, {
      startY: yPos + 4,
      head: [['Provider', 'Total', 'Verified', 'Unverified']],
      body: Object.entries(report.learners.byProvider || {}).map(([provider, values]) => ([
        provider,
        values.total,
        values.verified,
        values.unverified
      ])),
      styles: { fontSize: 9 },
      theme: 'grid'
    });
    yPos = doc.lastAutoTable.finalY + 6;
  }

  if (isVisible('sessions')) {
    doc.setFontSize(12);
    doc.text('Sessions Summary', 14, yPos);
    autoTable(doc, {
      startY: yPos + 4,
      head: [['Provider', 'Date', 'Time', 'Time Slot', 'Attendance', 'Notes']],
      body: report.sessions.items.length
        ? report.sessions.items.map((item) => ([
          item.provider,
          item.sessionDate,
          item.sessionTime,
          item.timeSlot,
          `${item.attendance.present} present / ${item.attendance.total} total`,
          item.notes || ''
        ]))
        : [['No sessions recorded in this range.', '', '', '', '', '']],
      styles: { fontSize: 8 },
      theme: 'grid'
    });
    yPos = doc.lastAutoTable.finalY + 6;
  }

  if (isVisible('signedAttendance')) {
    doc.setFontSize(12);
    doc.text('Signed Attendance Registers', 14, yPos);
    autoTable(doc, {
      startY: yPos + 4,
      head: [['Signed', 'Attended but not signed']],
      body: [[report.signedAttendance.signed, report.signedAttendance.attendedNotSigned]],
      styles: { fontSize: 9 },
      theme: 'grid'
    });
    yPos = doc.lastAutoTable.finalY + 6;
  }

  if (isVisible('announcements')) {
    doc.setFontSize(12);
    doc.text('Announcements', 14, yPos);
    autoTable(doc, {
      startY: yPos + 4,
      head: [['Subject', 'Sent At', 'Recipients', 'Providers']],
      body: report.announcements.items.length
        ? report.announcements.items.map((item) => ([
          item.subject,
          item.sentAt,
          item.recipientCount,
          (item.providers || []).join(', ')
        ]))
        : [['No announcements found.', '', '', '']],
      styles: { fontSize: 8 },
      theme: 'grid'
    });
    yPos = doc.lastAutoTable.finalY + 6;
  }

  if (isVisible('dropOffs')) {
    doc.setFontSize(12);
    doc.text('Drop-offs', 14, yPos);
    autoTable(doc, {
      startY: yPos + 4,
      head: [['Reason', 'Count']],
      body: report.dropOffs.reasons.length
        ? report.dropOffs.reasons.map((item) => [item.reason, item.count])
        : [['No drop-offs captured.', '']],
      styles: { fontSize: 9 },
      theme: 'grid'
    });
    yPos = doc.lastAutoTable.finalY + 6;

    if (report.dropOffs.items && report.dropOffs.items.length) {
      autoTable(doc, {
        startY: yPos + 2,
        head: [['Learner', 'Email', 'Cohort', 'Reason']],
        body: report.dropOffs.items.map((item) => [
          item.name,
          item.email,
          item.cohortName,
          item.reason
        ]),
        styles: { fontSize: 8 },
        theme: 'grid'
      });
      yPos = doc.lastAutoTable.finalY + 6;
    }
  }

  if (isVisible('communications')) {
    doc.setFontSize(12);
    doc.text('Communication via LMS', 14, yPos);
    autoTable(doc, {
      startY: yPos + 4,
      head: [['Metric', 'Value']],
      body: [
        ['Total Issues', report.issues.total],
        ['Primary Category', primaryCategory],
        ['Technical', report.issues.byCategory.technical],
        ['Classroom', report.issues.byCategory.classroom],
        ['Account', report.issues.byCategory.account],
        ['Other', report.issues.byCategory.other],
        ['Emails Sent', report.communications?.emailsSent || 0]
      ],
      styles: { fontSize: 9 },
      theme: 'grid'
    });
    yPos = doc.lastAutoTable.finalY + 6;

    autoTable(doc, {
      startY: yPos + 2,
      head: [['Resolved Queries']],
      body: report.issues.resolved.length
        ? report.issues.resolved.map((item) => [item.title])
        : [['No resolved queries recorded.']],
      styles: { fontSize: 9 },
      theme: 'grid'
    });
    yPos = doc.lastAutoTable.finalY + 6;

    if (report.communications?.items?.length) {
      autoTable(doc, {
        startY: yPos + 2,
        head: [['Email Subject', 'Sent At']],
        body: report.communications.items.map((item) => [item.subject, item.sentAt]),
        styles: { fontSize: 8 },
        theme: 'grid'
      });
      yPos = doc.lastAutoTable.finalY + 6;
    }
  }

  if (isVisible('weeklyDocuments') && report.weeklyDocuments) {
    doc.setFontSize(12);
    doc.text('Weekly Documents', 14, yPos);
    autoTable(doc, {
      startY: yPos + 4,
      head: [['Submitted']],
      body: [[report.weeklyDocuments.submitted]],
      styles: { fontSize: 9 },
      theme: 'grid'
    });
    yPos = doc.lastAutoTable.finalY + 6;

    autoTable(doc, {
      startY: yPos + 2,
      head: [['Submitted Learners']],
      body: [[(report.weeklyDocuments.submittedLearners || []).join(', ') || 'None']],
      styles: { fontSize: 8 },
      theme: 'grid'
    });
    yPos = doc.lastAutoTable.finalY + 6;
  }

  if (isVisible('facilitatorReports') && report.facilitatorReports) {
    doc.setFontSize(12);
    doc.text('Facilitator Reports', 14, yPos);
    autoTable(doc, {
      startY: yPos + 4,
      head: [['Facilitator', 'Reports']],
      body: report.facilitatorReports.byFacilitator?.length
        ? report.facilitatorReports.byFacilitator.map((item) => [item.name, item.count])
        : [['No facilitator reports recorded.', '']],
      styles: { fontSize: 9 },
      theme: 'grid'
    });
    yPos = doc.lastAutoTable.finalY + 6;

    if (report.facilitatorReports.items?.length) {
      autoTable(doc, {
        startY: yPos + 2,
        head: [['Cohort', 'Type', 'Range', 'Notes']],
        body: report.facilitatorReports.items.map((item) => {
          const range = item.type === 'weekly_highs_lows'
            ? `${item.weekStart || ''} - ${item.weekEnd || ''}`
            : (item.date || '');
          const detail = item.type === 'weekly_highs_lows'
            ? `Highs: ${item.weeklyHighs || ''} | Lows: ${item.weeklyLows || ''}`
            : `Comment: ${item.dailyComment || ''}`;
          return [item.cohortName, item.type, range, detail];
        }),
        styles: { fontSize: 8 },
        theme: 'grid'
      });
      yPos = doc.lastAutoTable.finalY + 6;
    }
  }

  if (isVisible('teamsMismatch') && report.teamsMismatch) {
    doc.setFontSize(12);
    doc.text('Teams Attendance Mismatches', 14, yPos);
    autoTable(doc, {
      startY: yPos + 4,
      head: [['Learner', 'Email', 'Session Cohort', 'Learner Cohort']],
      body: report.teamsMismatch.items?.length
        ? report.teamsMismatch.items.map((item) => [
          item.learnerName,
          item.email,
          item.sessionCohortName,
          item.learnerCohortName
        ])
        : [['No mismatches detected.', '', '', '']],
      styles: { fontSize: 8 },
      theme: 'grid'
    });
  }

  doc.save(filename);
};

const CUSTOM_SESSION_ID = 'custom-session';

const QAReportCenter = () => {
  const [reportType, setReportType] = useState('daily');
  const [reportDate, setReportDate] = useState('');
  const [reportNotes, setReportNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [editableReport, setEditableReport] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [sectionVisibility, setSectionVisibility] = useState({
    keyMetrics: true,
    learnerSummary: true,
    sessions: true,
    attendanceByLearner: true,
    signedAttendance: true,
    announcements: true,
    dropOffs: true,
    communications: true,
    weeklyDocuments: true,
    facilitatorReports: true,
    teamsMismatch: true
  });
  const [cohorts, setCohorts] = useState([]);
  const [selectedCohortIds, setSelectedCohortIds] = useState([]);
  const [loadingCohorts, setLoadingCohorts] = useState(false);

  const buildWeeklyTemplate = (reportData) => {
    const attendanceRate = reportData?.attendance?.total
      ? Math.round((reportData.attendance.present / reportData.attendance.total) * 100)
      : '';
    return {
      weekNumber: '',
      weekEnding: reportData?.rangeEnd ? reportData.rangeEnd.split('T')[0] : '',
      currentPhase: '',
      submittedBy: '',
      weeklySummary: '',
      activities: [
        { activity: '', plannedActual: '', status: '' },
        { activity: '', plannedActual: '', status: '' },
        { activity: '', plannedActual: '', status: '' }
      ],
      attendanceMetrics: [
        { metric: 'Active learners (attending)', thisWeek: reportData?.attendance?.present || '', lastWeek: '', trend: '' },
        { metric: 'Average daily attendance rate (%)', thisWeek: attendanceRate, lastWeek: '', trend: '' },
        { metric: 'Learners with perfect attendance', thisWeek: '', lastWeek: '', trend: '' },
        { metric: 'Learners with attendance concerns (<75%)', thisWeek: '', lastWeek: '', trend: '' },
        { metric: 'New dropouts this week', thisWeek: reportData?.dropOffs?.total || '', lastWeek: '', trend: '' },
        { metric: 'Total cumulative dropouts', thisWeek: reportData?.dropOffs?.total || '', lastWeek: '', trend: '' }
      ],
      attendanceNotes: '',
      assessments: [
        { activity: 'Knowledge questionnaires administered', completed: '', comments: '' },
        { activity: 'Workplace assignments completed', completed: '', comments: '' },
        { activity: 'Practical observations conducted', completed: '', comments: '' },
        { activity: 'POE submissions reviewed/signed off', completed: '', comments: '' }
      ],
      challenges: ['', '', ''],
      escalations: '',
      nextWeekPlan: ['', '', '', ''],
      quickWins: ['', '', '']
    };
  };

  useEffect(() => {
    const loadCohorts = async () => {
      try {
        setLoadingCohorts(true);
        const snapshot = await getDocs(collection(db, 'cohorts'));
        const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCohorts(list);
      } catch (err) {
        setError(err.message || 'Failed to load cohorts.');
      } finally {
        setLoadingCohorts(false);
      }
    };

    loadCohorts();
  }, []);

  const toggleCohort = (cohortId) => {
    setSelectedCohortIds((prev) => (
      prev.includes(cohortId)
        ? prev.filter(id => id !== cohortId)
        : [...prev, cohortId]
    ));
  };

  const selectAllCohorts = () => {
    setSelectedCohortIds([...cohorts.map(c => c.id), CUSTOM_SESSION_ID]);
  };

  const clearCohorts = () => {
    setSelectedCohortIds([]);
  };

  const getSelectedCohortNames = () => (
    selectedCohortIds
      .map((id) => (id === CUSTOM_SESSION_ID ? 'Custom Sessions' : cohorts.find(c => c.id === id)?.name))
      .filter(Boolean)
  );

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError('');
      if (!selectedCohortIds.length) {
        setError('Select at least one cohort before generating the report.');
        return;
      }
      const response = await qaReportService.generateReport(
        reportType,
        reportDate,
        reportNotes,
        selectedCohortIds
      );
      setReport(response.report);
      const cloned = JSON.parse(JSON.stringify(response.report));
      if (reportType === 'weekly') {
        cloned.weeklyTemplate = cloned.weeklyTemplate || buildWeeklyTemplate(cloned);
      }
      setEditableReport(cloned);
      setIsEditing(false);
    } catch (err) {
      setError(err.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const getActiveReport = () => editableReport || report;

  const handleExportCsv = () => {
    const activeReport = getActiveReport();
    if (!activeReport) return;
    const rows = buildCsvRows(activeReport, sectionVisibility);
    downloadCsv(rows, `qa_${reportType}_report_${report.reportDate}.csv`);
  };

  const handleExportPdf = async () => {
    const activeReport = getActiveReport();
    if (!activeReport) return;
    await exportReportPdf(activeReport, `qa_${reportType}_report_${report.reportDate}.pdf`, sectionVisibility);
  };

  return (
    <Card>
      <Card.Header>
        <h5 className="mb-0">QA Reports</h5>
      </Card.Header>
      <Card.Body>
        <div className="mb-4">
          <DailyReportLog />
        </div>
        <Form className="row g-3 mb-3">
          <div className="col-12">
            <Form.Label>Cohort Scope (required)</Form.Label>
            <div className="d-flex flex-wrap gap-2 mb-2">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={selectAllCohorts}
                disabled={loadingCohorts || cohorts.length === 0}
              >
                Select all
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={clearCohorts}
                disabled={selectedCohortIds.length === 0}
              >
                Clear
              </Button>
              <Badge bg="light" text="dark" className="align-self-center">
                {selectedCohortIds.length || '0'} selected
              </Badge>
            </div>
            <div className="border rounded p-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
              {loadingCohorts ? (
                <div className="text-muted">Loading cohorts...</div>
              ) : cohorts.length === 0 ? (
                <div className="text-muted">No cohorts found.</div>
              ) : (
                <>
                  <Form.Check
                    type="checkbox"
                    id="qa-report-cohort-custom"
                    label="Custom Sessions (No Cohort)"
                    checked={selectedCohortIds.includes(CUSTOM_SESSION_ID)}
                    onChange={() => toggleCohort(CUSTOM_SESSION_ID)}
                  />
                  {cohorts.map((cohort) => (
                    <Form.Check
                      key={cohort.id}
                      type="checkbox"
                      id={`qa-report-cohort-${cohort.id}`}
                      label={cohort.name || 'Unnamed cohort'}
                      checked={selectedCohortIds.includes(cohort.id)}
                      onChange={() => toggleCohort(cohort.id)}
                    />
                  ))}
                </>
              )}
            </div>
          </div>
          <div className="col-md-4">
            <Form.Label>Report Type</Form.Label>
            <Form.Select value={reportType} onChange={(e) => setReportType(e.target.value)}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </Form.Select>
          </div>
          <div className="col-md-4">
            <Form.Label>Report Date (optional)</Form.Label>
            <Form.Control
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
            />
          </div>
          <div className="col-md-4 d-flex align-items-end">
            <Button variant="primary" onClick={handleGenerate} disabled={loading}>
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
          </div>
          <div className="col-12">
            <Form.Label>Report Notes (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={reportNotes}
              onChange={(e) => setReportNotes(e.target.value)}
              placeholder="Add any notes or context to include with this report."
            />
          </div>
        </Form>

        {error && <Alert variant="danger">{error}</Alert>}

        {report && (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Form.Check
                type="switch"
                id="qa-report-edit-mode"
                label="Edit mode"
                checked={isEditing}
                onChange={(e) => setIsEditing(e.target.checked)}
              />
            </div>

            <Card className="mb-3">
              <Card.Header>
                <strong>Customize Sections</strong>
              </Card.Header>
              <Card.Body className="d-flex flex-wrap gap-3">
                {[
                  { key: 'keyMetrics', label: 'Key Metrics' },
                  { key: 'learnerSummary', label: 'Learner Summary' },
                  { key: 'sessions', label: 'Sessions' },
                  { key: 'attendanceByLearner', label: 'Attendance By Learner' },
                  { key: 'signedAttendance', label: 'Signed Attendance' },
                  { key: 'announcements', label: 'Announcements' },
                  { key: 'dropOffs', label: 'Drop-offs' },
                  { key: 'communications', label: 'Communication' },
                  { key: 'weeklyDocuments', label: 'Weekly Documents' },
                  { key: 'facilitatorReports', label: 'Facilitator Reports' },
                  { key: 'teamsMismatch', label: 'Teams Mismatches' }
                ].map((item) => (
                  <Form.Check
                    key={item.key}
                    type="checkbox"
                    label={item.label}
                    checked={sectionVisibility[item.key]}
                    onChange={() =>
                      setSectionVisibility((prev) => ({
                        ...prev,
                        [item.key]: !prev[item.key]
                      }))
                    }
                  />
                ))}
              </Card.Body>
            </Card>

            {isEditing && editableReport && (
              <Card className="mb-3">
                <Card.Header>
                  <strong>Override Values</strong>
                </Card.Header>
                <Card.Body>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <Form.Label>Learners Total</Form.Label>
                      <Form.Control
                        type="number"
                        value={editableReport.learners.total}
                        onChange={(e) =>
                          setEditableReport((prev) => ({
                            ...prev,
                            learners: { ...prev.learners, total: Number(e.target.value) }
                          }))
                        }
                      />
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Verified</Form.Label>
                      <Form.Control
                        type="number"
                        value={editableReport.learners.verified}
                        onChange={(e) =>
                          setEditableReport((prev) => ({
                            ...prev,
                            learners: { ...prev.learners, verified: Number(e.target.value) }
                          }))
                        }
                      />
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Unverified</Form.Label>
                      <Form.Control
                        type="number"
                        value={editableReport.learners.unverified}
                        onChange={(e) =>
                          setEditableReport((prev) => ({
                            ...prev,
                            learners: { ...prev.learners, unverified: Number(e.target.value) }
                          }))
                        }
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Attendance Total</Form.Label>
                      <Form.Control
                        type="number"
                        value={editableReport.attendance.total}
                        onChange={(e) =>
                          setEditableReport((prev) => ({
                            ...prev,
                            attendance: { ...prev.attendance, total: Number(e.target.value) }
                          }))
                        }
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Present</Form.Label>
                      <Form.Control
                        type="number"
                        value={editableReport.attendance.present}
                        onChange={(e) =>
                          setEditableReport((prev) => ({
                            ...prev,
                            attendance: { ...prev.attendance, present: Number(e.target.value) }
                          }))
                        }
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Absent</Form.Label>
                      <Form.Control
                        type="number"
                        value={editableReport.attendance.absent}
                        onChange={(e) =>
                          setEditableReport((prev) => ({
                            ...prev,
                            attendance: { ...prev.attendance, absent: Number(e.target.value) }
                          }))
                        }
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Excused</Form.Label>
                      <Form.Control
                        type="number"
                        value={editableReport.attendance.excused}
                        onChange={(e) =>
                          setEditableReport((prev) => ({
                            ...prev,
                            attendance: { ...prev.attendance, excused: Number(e.target.value) }
                          }))
                        }
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Incomplete</Form.Label>
                      <Form.Control
                        type="number"
                        value={editableReport.attendance.incomplete}
                        onChange={(e) =>
                          setEditableReport((prev) => ({
                            ...prev,
                            attendance: { ...prev.attendance, incomplete: Number(e.target.value) }
                          }))
                        }
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Signed Attendance</Form.Label>
                      <Form.Control
                        type="number"
                        value={editableReport.signedAttendance.signed}
                        onChange={(e) =>
                          setEditableReport((prev) => ({
                            ...prev,
                            signedAttendance: { ...prev.signedAttendance, signed: Number(e.target.value) }
                          }))
                        }
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Attended not signed</Form.Label>
                      <Form.Control
                        type="number"
                        value={editableReport.signedAttendance.attendedNotSigned}
                        onChange={(e) =>
                          setEditableReport((prev) => ({
                            ...prev,
                            signedAttendance: { ...prev.signedAttendance, attendedNotSigned: Number(e.target.value) }
                          }))
                        }
                      />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}

            {isEditing && editableReport?.weeklyTemplate && (
              <Card className="mb-3">
                <Card.Header>
                  <strong>Weekly Progress Update (Editable)</strong>
                </Card.Header>
                <Card.Body>
                  <Row className="mb-3">
                    <Col md={3}>
                      <Form.Label>Week #</Form.Label>
                      <Form.Control
                        value={editableReport.weeklyTemplate.weekNumber}
                        onChange={(e) =>
                          setEditableReport((prev) => ({
                            ...prev,
                            weeklyTemplate: { ...prev.weeklyTemplate, weekNumber: e.target.value }
                          }))
                        }
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Week Ending</Form.Label>
                      <Form.Control
                        value={editableReport.weeklyTemplate.weekEnding}
                        onChange={(e) =>
                          setEditableReport((prev) => ({
                            ...prev,
                            weeklyTemplate: { ...prev.weeklyTemplate, weekEnding: e.target.value }
                          }))
                        }
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Current Phase</Form.Label>
                      <Form.Control
                        value={editableReport.weeklyTemplate.currentPhase}
                        onChange={(e) =>
                          setEditableReport((prev) => ({
                            ...prev,
                            weeklyTemplate: { ...prev.weeklyTemplate, currentPhase: e.target.value }
                          }))
                        }
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Submitted By</Form.Label>
                      <Form.Control
                        value={editableReport.weeklyTemplate.submittedBy}
                        onChange={(e) =>
                          setEditableReport((prev) => ({
                            ...prev,
                            weeklyTemplate: { ...prev.weeklyTemplate, submittedBy: e.target.value }
                          }))
                        }
                      />
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Weekly Summary</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={editableReport.weeklyTemplate.weeklySummary}
                      onChange={(e) =>
                        setEditableReport((prev) => ({
                          ...prev,
                          weeklyTemplate: { ...prev.weeklyTemplate, weeklySummary: e.target.value }
                        }))
                      }
                    />
                  </Form.Group>

                  <h6>Activities</h6>
                  <Table size="sm" bordered>
                    <thead>
                      <tr>
                        <th>Activity / Training Session</th>
                        <th>Planned / Actual</th>
                        <th>Status</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {editableReport.weeklyTemplate.activities.map((item, index) => (
                        <tr key={`activity-${index}`}>
                          <td>
                            <Form.Control
                              size="sm"
                              value={item.activity}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const activities = [...prev.weeklyTemplate.activities];
                                  activities[index] = { ...activities[index], activity: e.target.value };
                                  return { ...prev, weeklyTemplate: { ...prev.weeklyTemplate, activities } };
                                })
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={item.plannedActual}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const activities = [...prev.weeklyTemplate.activities];
                                  activities[index] = { ...activities[index], plannedActual: e.target.value };
                                  return { ...prev, weeklyTemplate: { ...prev.weeklyTemplate, activities } };
                                })
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={item.status}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const activities = [...prev.weeklyTemplate.activities];
                                  activities[index] = { ...activities[index], status: e.target.value };
                                  return { ...prev, weeklyTemplate: { ...prev.weeklyTemplate, activities } };
                                })
                              }
                            />
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() =>
                                setEditableReport((prev) => {
                                  const activities = prev.weeklyTemplate.activities.filter((_, i) => i !== index);
                                  return { ...prev, weeklyTemplate: { ...prev.weeklyTemplate, activities } };
                                })
                              }
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="mb-3"
                    onClick={() =>
                      setEditableReport((prev) => ({
                        ...prev,
                        weeklyTemplate: {
                          ...prev.weeklyTemplate,
                          activities: [...prev.weeklyTemplate.activities, { activity: '', plannedActual: '', status: '' }]
                        }
                      }))
                    }
                  >
                    Add Activity
                  </Button>

                  <h6>Attendance Metrics</h6>
                  <Table size="sm" bordered>
                    <thead>
                      <tr>
                        <th>Metric</th>
                        <th>This Week</th>
                        <th>Last Week</th>
                        <th>Trend</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {editableReport.weeklyTemplate.attendanceMetrics.map((item, index) => (
                        <tr key={`attendance-${index}`}>
                          <td>
                            <Form.Control
                              size="sm"
                              value={item.metric}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const attendanceMetrics = [...prev.weeklyTemplate.attendanceMetrics];
                                  attendanceMetrics[index] = { ...attendanceMetrics[index], metric: e.target.value };
                                  return { ...prev, weeklyTemplate: { ...prev.weeklyTemplate, attendanceMetrics } };
                                })
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={item.thisWeek}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const attendanceMetrics = [...prev.weeklyTemplate.attendanceMetrics];
                                  attendanceMetrics[index] = { ...attendanceMetrics[index], thisWeek: e.target.value };
                                  return { ...prev, weeklyTemplate: { ...prev.weeklyTemplate, attendanceMetrics } };
                                })
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={item.lastWeek}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const attendanceMetrics = [...prev.weeklyTemplate.attendanceMetrics];
                                  attendanceMetrics[index] = { ...attendanceMetrics[index], lastWeek: e.target.value };
                                  return { ...prev, weeklyTemplate: { ...prev.weeklyTemplate, attendanceMetrics } };
                                })
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={item.trend}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const attendanceMetrics = [...prev.weeklyTemplate.attendanceMetrics];
                                  attendanceMetrics[index] = { ...attendanceMetrics[index], trend: e.target.value };
                                  return { ...prev, weeklyTemplate: { ...prev.weeklyTemplate, attendanceMetrics } };
                                })
                              }
                            />
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() =>
                                setEditableReport((prev) => {
                                  const attendanceMetrics = prev.weeklyTemplate.attendanceMetrics.filter((_, i) => i !== index);
                                  return { ...prev, weeklyTemplate: { ...prev.weeklyTemplate, attendanceMetrics } };
                                })
                              }
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="mb-3"
                    onClick={() =>
                      setEditableReport((prev) => ({
                        ...prev,
                        weeklyTemplate: {
                          ...prev.weeklyTemplate,
                          attendanceMetrics: [
                            ...prev.weeklyTemplate.attendanceMetrics,
                            { metric: '', thisWeek: '', lastWeek: '', trend: '' }
                          ]
                        }
                      }))
                    }
                  >
                    Add Metric
                  </Button>

                  <Form.Group className="mb-3">
                    <Form.Label>Attendance Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={editableReport.weeklyTemplate.attendanceNotes}
                      onChange={(e) =>
                        setEditableReport((prev) => ({
                          ...prev,
                          weeklyTemplate: { ...prev.weeklyTemplate, attendanceNotes: e.target.value }
                        }))
                      }
                    />
                  </Form.Group>

                  <h6>Assessment Progress</h6>
                  <Table size="sm" bordered>
                    <thead>
                      <tr>
                        <th>Assessment Activity</th>
                        <th>Completed</th>
                        <th>Status / Comments</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {editableReport.weeklyTemplate.assessments.map((item, index) => (
                        <tr key={`assessment-${index}`}>
                          <td>
                            <Form.Control
                              size="sm"
                              value={item.activity}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const assessments = [...prev.weeklyTemplate.assessments];
                                  assessments[index] = { ...assessments[index], activity: e.target.value };
                                  return { ...prev, weeklyTemplate: { ...prev.weeklyTemplate, assessments } };
                                })
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={item.completed}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const assessments = [...prev.weeklyTemplate.assessments];
                                  assessments[index] = { ...assessments[index], completed: e.target.value };
                                  return { ...prev, weeklyTemplate: { ...prev.weeklyTemplate, assessments } };
                                })
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={item.comments}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const assessments = [...prev.weeklyTemplate.assessments];
                                  assessments[index] = { ...assessments[index], comments: e.target.value };
                                  return { ...prev, weeklyTemplate: { ...prev.weeklyTemplate, assessments } };
                                })
                              }
                            />
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() =>
                                setEditableReport((prev) => {
                                  const assessments = prev.weeklyTemplate.assessments.filter((_, i) => i !== index);
                                  return { ...prev, weeklyTemplate: { ...prev.weeklyTemplate, assessments } };
                                })
                              }
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="mb-3"
                    onClick={() =>
                      setEditableReport((prev) => ({
                        ...prev,
                        weeklyTemplate: {
                          ...prev.weeklyTemplate,
                          assessments: [...prev.weeklyTemplate.assessments, { activity: '', completed: '', comments: '' }]
                        }
                      }))
                    }
                  >
                    Add Assessment
                  </Button>

                  <Form.Group className="mb-3">
                    <Form.Label>Challenges & Issues (one per line)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={(editableReport.weeklyTemplate.challenges || []).join('\n')}
                      onChange={(e) =>
                        setEditableReport((prev) => ({
                          ...prev,
                          weeklyTemplate: { ...prev.weeklyTemplate, challenges: e.target.value.split('\n').filter(Boolean) }
                        }))
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Escalations Required</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={editableReport.weeklyTemplate.escalations}
                      onChange={(e) =>
                        setEditableReport((prev) => ({
                          ...prev,
                          weeklyTemplate: { ...prev.weeklyTemplate, escalations: e.target.value }
                        }))
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Next Week's Plan (one per line)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={(editableReport.weeklyTemplate.nextWeekPlan || []).join('\n')}
                      onChange={(e) =>
                        setEditableReport((prev) => ({
                          ...prev,
                          weeklyTemplate: { ...prev.weeklyTemplate, nextWeekPlan: e.target.value.split('\n').filter(Boolean) }
                        }))
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Quick Wins & Highlights (one per line)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={(editableReport.weeklyTemplate.quickWins || []).join('\n')}
                      onChange={(e) =>
                        setEditableReport((prev) => ({
                          ...prev,
                          weeklyTemplate: { ...prev.weeklyTemplate, quickWins: e.target.value.split('\n').filter(Boolean) }
                        }))
                      }
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            )}

            <div className="d-flex flex-wrap gap-2 mb-3">
              <Badge bg="secondary">{report.title}</Badge>
              <Badge bg="info">{report.rangeStart.split('T')[0]} to {report.rangeEnd.split('T')[0]}</Badge>
              {report.notes && <Badge bg="light" text="dark">Notes included</Badge>}
            </div>
            {getSelectedCohortNames().length > 0 && (
              <Alert variant="info">
                <strong>Cohorts:</strong> {getSelectedCohortNames().join(', ')}
              </Alert>
            )}

            <div className="d-flex gap-2 mb-3">
              <Button variant="outline-secondary" onClick={handleExportPdf}>Export PDF</Button>
              <Button variant="outline-secondary" onClick={handleExportCsv}>Export CSV</Button>
            </div>

            {sectionVisibility.keyMetrics && (
              <>
                <h6>Key Metrics</h6>
                <Table size="sm" bordered>
                  <tbody>
                    <tr>
                      <td>Learners Verified</td>
                      <td>{(editableReport || report).learners.verified}/{(editableReport || report).learners.total}</td>
                    </tr>
                    <tr>
                      <td>Attendance Records</td>
                      <td>{(editableReport || report).attendance.present} present / {(editableReport || report).attendance.total} total</td>
                    </tr>
                    <tr>
                      <td>Signed Attendance</td>
                      <td>{(editableReport || report).signedAttendance.signed}</td>
                    </tr>
                    <tr>
                      <td>Announcements Sent</td>
                      <td>{(editableReport || report).announcements.total}</td>
                    </tr>
                    <tr>
                      <td>Issues Logged</td>
                      <td>{(editableReport || report).issues.total}</td>
                    </tr>
                    <tr>
                      <td>Teams Cohort Mismatches</td>
                      <td>{(editableReport || report).teamsMismatch.total}</td>
                    </tr>
                  </tbody>
                </Table>
              </>
            )}

            {sectionVisibility.learnerSummary && (
              <>
                <h6>Learner Verification Summary</h6>
                <Table size="sm" bordered>
                  <thead>
                    <tr>
                      <th>Provider</th>
                      <th>Total</th>
                      <th>Verified</th>
                      <th>Unverified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries((editableReport || report).learners.byProvider || {}).map(([provider, values]) => (
                      <tr key={provider}>
                        <td>{provider}</td>
                        <td>{values.total}</td>
                        <td>{values.verified}</td>
                        <td>{values.unverified}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}

            {sectionVisibility.sessions && (
              <>
                <h6>Sessions Summary</h6>
                {isEditing && (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="mb-2"
                    onClick={() =>
                      setEditableReport((prev) => ({
                        ...prev,
                        sessions: {
                          ...prev.sessions,
                          items: [
                            ...prev.sessions.items,
                            {
                              provider: '',
                              moduleName: '',
                              sessionDate: '',
                              sessionTime: '',
                              timeSlot: '',
                              attendance: { present: 0, total: 30 },
                              notes: ''
                            }
                          ]
                        }
                      }))
                    }
                  >
                    Add Session
                  </Button>
                )}
                <Table size="sm" bordered>
                  <thead>
                    <tr>
                      <th>Provider</th>
                      <th>Session</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Time Slot</th>
                      <th>Attendance</th>
                      <th>Notes</th>
                      {isEditing && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {(editableReport || report).sessions.items.map((session, index) => (
                      <tr key={session.sessionId || `${session.moduleName}-${index}`}>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              value={session.provider}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const items = [...prev.sessions.items];
                                  items[index] = { ...items[index], provider: e.target.value };
                                  return { ...prev, sessions: { ...prev.sessions, items } };
                                })
                              }
                            />
                          ) : (
                            session.provider
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              value={session.moduleName || ''}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const items = [...prev.sessions.items];
                                  items[index] = { ...items[index], moduleName: e.target.value };
                                  return { ...prev, sessions: { ...prev.sessions, items } };
                                })
                              }
                            />
                          ) : (
                            session.moduleName || ''
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              value={session.sessionDate}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const items = [...prev.sessions.items];
                                  items[index] = { ...items[index], sessionDate: e.target.value };
                                  return { ...prev, sessions: { ...prev.sessions, items } };
                                })
                              }
                            />
                          ) : (
                            session.sessionDate
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              value={session.sessionTime}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const items = [...prev.sessions.items];
                                  items[index] = { ...items[index], sessionTime: e.target.value };
                                  return { ...prev, sessions: { ...prev.sessions, items } };
                                })
                              }
                            />
                          ) : (
                            session.sessionTime
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              value={session.timeSlot}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const items = [...prev.sessions.items];
                                  items[index] = { ...items[index], timeSlot: e.target.value };
                                  return { ...prev, sessions: { ...prev.sessions, items } };
                                })
                              }
                            />
                          ) : (
                            session.timeSlot
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <div className="d-flex gap-2">
                              <Form.Control
                                size="sm"
                                type="number"
                                value={session.attendance.present}
                                onChange={(e) =>
                                  setEditableReport((prev) => {
                                    const items = [...prev.sessions.items];
                                    items[index] = {
                                      ...items[index],
                                      attendance: {
                                        ...items[index].attendance,
                                        present: Number(e.target.value)
                                      }
                                    };
                                    return { ...prev, sessions: { ...prev.sessions, items } };
                                  })
                                }
                              />
                              <Form.Control
                                size="sm"
                                type="number"
                                value={session.attendance.total}
                                onChange={(e) =>
                                  setEditableReport((prev) => {
                                    const items = [...prev.sessions.items];
                                    items[index] = {
                                      ...items[index],
                                      attendance: {
                                        ...items[index].attendance,
                                        total: Number(e.target.value)
                                      }
                                    };
                                    return { ...prev, sessions: { ...prev.sessions, items } };
                                  })
                                }
                              />
                            </div>
                          ) : (
                            `${session.attendance.present} present / ${session.attendance.total} total`
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              value={session.notes || ''}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const items = [...prev.sessions.items];
                                  items[index] = { ...items[index], notes: e.target.value };
                                  return { ...prev, sessions: { ...prev.sessions, items } };
                                })
                              }
                            />
                          ) : (
                            session.notes || ''
                          )}
                        </td>
                        {isEditing && (
                          <td>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() =>
                                setEditableReport((prev) => {
                                  const items = prev.sessions.items.filter((_, i) => i !== index);
                                  return { ...prev, sessions: { ...prev.sessions, items } };
                                })
                              }
                            >
                              Remove
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {(editableReport || report).sessions.items.length === 0 && (
                      <tr>
                        <td colSpan={isEditing ? 8 : 7} className="text-muted text-center">No sessions recorded in this range.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </>
            )}

            {sectionVisibility.attendanceByLearner && (editableReport || report).attendanceByLearner && (
              <>
                <h6>Attendance by Learner</h6>
                <Table size="sm" bordered>
                  <thead>
                    <tr>
                      <th>Learner</th>
                      <th>Email</th>
                      <th>Cohort</th>
                      <th>Present</th>
                      <th>Total</th>
                      <th>Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(editableReport || report).attendanceByLearner.map((item) => (
                      <tr key={item.learnerId || item.email}>
                        <td>{item.name}</td>
                        <td>{item.email}</td>
                        <td>{item.cohortName || ''}</td>
                        <td>{item.present}</td>
                        <td>{item.total}</td>
                        <td>{item.attendanceRate}%</td>
                      </tr>
                    ))}
                    {(editableReport || report).attendanceByLearner.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-muted text-center">No attendance records.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </>
            )}

            {sectionVisibility.signedAttendance && (
              <>
                <h6>Signed Attendance Registers</h6>
                <Table size="sm" bordered>
                  <tbody>
                    <tr>
                      <td>Signed</td>
                      <td>{(editableReport || report).signedAttendance.signed}</td>
                    </tr>
                    <tr>
                      <td>Attended but not signed</td>
                      <td>{(editableReport || report).signedAttendance.attendedNotSigned}</td>
                    </tr>
                  </tbody>
                </Table>
              </>
            )}

            {sectionVisibility.announcements && (
              <>
                <h6>Announcements</h6>
                {isEditing && (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="mb-2"
                    onClick={() =>
                      setEditableReport((prev) => ({
                        ...prev,
                        announcements: {
                          ...prev.announcements,
                          items: [
                            ...prev.announcements.items,
                            { subject: '', sentAt: '', recipientCount: 0, providers: [] }
                          ]
                        }
                      }))
                    }
                  >
                    Add Announcement
                  </Button>
                )}
                <Table size="sm" bordered>
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th>Sent At</th>
                      <th>Recipients</th>
                      <th>Providers</th>
                      {isEditing && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {(editableReport || report).announcements.items.map((item, index) => (
                      <tr key={`${item.subject}-${index}`}>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              value={item.subject || ''}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const items = [...prev.announcements.items];
                                  items[index] = { ...items[index], subject: e.target.value };
                                  return { ...prev, announcements: { ...prev.announcements, items } };
                                })
                              }
                            />
                          ) : item.subject}
                        </td>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              value={item.sentAt || ''}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const items = [...prev.announcements.items];
                                  items[index] = { ...items[index], sentAt: e.target.value };
                                  return { ...prev, announcements: { ...prev.announcements, items } };
                                })
                              }
                            />
                          ) : item.sentAt}
                        </td>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              type="number"
                              value={item.recipientCount || 0}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const items = [...prev.announcements.items];
                                  items[index] = { ...items[index], recipientCount: Number(e.target.value) };
                                  return { ...prev, announcements: { ...prev.announcements, items } };
                                })
                              }
                            />
                          ) : item.recipientCount}
                        </td>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              value={(item.providers || []).join(', ')}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const items = [...prev.announcements.items];
                                  items[index] = { ...items[index], providers: e.target.value.split(',').map(v => v.trim()).filter(Boolean) };
                                  return { ...prev, announcements: { ...prev.announcements, items } };
                                })
                              }
                            />
                          ) : ((item.providers || []).join(', ') || 'N/A')}
                        </td>
                        {isEditing && (
                          <td>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() =>
                                setEditableReport((prev) => {
                                  const items = prev.announcements.items.filter((_, i) => i !== index);
                                  return { ...prev, announcements: { ...prev.announcements, items } };
                                })
                              }
                            >
                              Remove
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {(editableReport || report).announcements.items.length === 0 && (
                      <tr>
                        <td colSpan={isEditing ? 5 : 4} className="text-muted text-center">No announcements found.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </>
            )}

            {sectionVisibility.dropOffs && (
              <>
                <h6>Drop-offs</h6>
                {isEditing && (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="mb-2"
                    onClick={() =>
                      setEditableReport((prev) => ({
                        ...prev,
                        dropOffs: {
                          ...prev.dropOffs,
                          reasons: [...prev.dropOffs.reasons, { reason: '', count: 0 }]
                        }
                      }))
                    }
                  >
                    Add Reason
                  </Button>
                )}
                <Table size="sm" bordered>
                  <thead>
                    <tr>
                      <th>Reason</th>
                      <th>Count</th>
                      {isEditing && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {(editableReport || report).dropOffs.reasons.map((item, index) => (
                      <tr key={`${item.reason}-${index}`}>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              value={item.reason}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const reasons = [...prev.dropOffs.reasons];
                                  reasons[index] = { ...reasons[index], reason: e.target.value };
                                  return { ...prev, dropOffs: { ...prev.dropOffs, reasons } };
                                })
                              }
                            />
                          ) : item.reason}
                        </td>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              type="number"
                              value={item.count}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const reasons = [...prev.dropOffs.reasons];
                                  reasons[index] = { ...reasons[index], count: Number(e.target.value) };
                                  return { ...prev, dropOffs: { ...prev.dropOffs, reasons } };
                                })
                              }
                            />
                          ) : item.count}
                        </td>
                        {isEditing && (
                          <td>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() =>
                                setEditableReport((prev) => {
                                  const reasons = prev.dropOffs.reasons.filter((_, i) => i !== index);
                                  return { ...prev, dropOffs: { ...prev.dropOffs, reasons } };
                                })
                              }
                            >
                              Remove
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {(editableReport || report).dropOffs.reasons.length === 0 && (
                      <tr>
                        <td colSpan={isEditing ? 3 : 2} className="text-muted text-center">No drop-offs captured.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                {(editableReport || report).dropOffs.items?.length > 0 && (
                  <>
                    {isEditing && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="mb-2"
                        onClick={() =>
                          setEditableReport((prev) => ({
                            ...prev,
                            dropOffs: {
                              ...prev.dropOffs,
                              items: [
                                ...prev.dropOffs.items,
                                { name: '', email: '', cohortName: '', reason: '' }
                              ]
                            }
                          }))
                        }
                      >
                        Add Learner
                      </Button>
                    )}
                  <Table size="sm" bordered>
                    <thead>
                      <tr>
                        <th>Learner</th>
                        <th>Email</th>
                        <th>Cohort</th>
                        <th>Reason</th>
                        {isEditing && <th />}
                      </tr>
                    </thead>
                    <tbody>
                      {(editableReport || report).dropOffs.items.map((item, index) => (
                        <tr key={`${item.email}-${index}`}>
                          <td>
                            {isEditing ? (
                              <Form.Control
                                size="sm"
                                value={item.name}
                                onChange={(e) =>
                                  setEditableReport((prev) => {
                                    const items = [...prev.dropOffs.items];
                                    items[index] = { ...items[index], name: e.target.value };
                                    return { ...prev, dropOffs: { ...prev.dropOffs, items } };
                                  })
                                }
                              />
                            ) : item.name}
                          </td>
                          <td>
                            {isEditing ? (
                              <Form.Control
                                size="sm"
                                value={item.email || ''}
                                onChange={(e) =>
                                  setEditableReport((prev) => {
                                    const items = [...prev.dropOffs.items];
                                    items[index] = { ...items[index], email: e.target.value };
                                    return { ...prev, dropOffs: { ...prev.dropOffs, items } };
                                  })
                                }
                              />
                            ) : (item.email || 'N/A')}
                          </td>
                          <td>
                            {isEditing ? (
                              <Form.Control
                                size="sm"
                                value={item.cohortName || ''}
                                onChange={(e) =>
                                  setEditableReport((prev) => {
                                    const items = [...prev.dropOffs.items];
                                    items[index] = { ...items[index], cohortName: e.target.value };
                                    return { ...prev, dropOffs: { ...prev.dropOffs, items } };
                                  })
                                }
                              />
                            ) : (item.cohortName || 'Unknown')}
                          </td>
                          <td>
                            {isEditing ? (
                              <Form.Control
                                size="sm"
                                value={item.reason}
                                onChange={(e) =>
                                  setEditableReport((prev) => {
                                    const items = [...prev.dropOffs.items];
                                    items[index] = { ...items[index], reason: e.target.value };
                                    return { ...prev, dropOffs: { ...prev.dropOffs, items } };
                                  })
                                }
                              />
                            ) : item.reason}
                          </td>
                          {isEditing && (
                            <td>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() =>
                                  setEditableReport((prev) => {
                                    const items = prev.dropOffs.items.filter((_, i) => i !== index);
                                    return { ...prev, dropOffs: { ...prev.dropOffs, items } };
                                  })
                                }
                              >
                                Remove
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  </>
                )}
              </>
            )}

            {sectionVisibility.communications && (
              <>
                <h6>Communication via LMS (Issues)</h6>
                <Table size="sm" bordered>
                  <tbody>
                    <tr>
                      <td>Total Issues</td>
                      <td>
                        {isEditing ? (
                          <Form.Control
                            size="sm"
                            type="number"
                            value={(editableReport || report).issues.total}
                            onChange={(e) =>
                              setEditableReport((prev) => ({
                                ...prev,
                                issues: { ...prev.issues, total: Number(e.target.value) }
                              }))
                            }
                          />
                        ) : (editableReport || report).issues.total}
                      </td>
                    </tr>
                    <tr>
                      <td>Primary Category</td>
                      <td>
                        {isEditing ? (
                          <Form.Control
                            size="sm"
                            value={(editableReport || report).issues.primaryCategory}
                            onChange={(e) =>
                              setEditableReport((prev) => ({
                                ...prev,
                                issues: { ...prev.issues, primaryCategory: e.target.value }
                              }))
                            }
                          />
                        ) : (editableReport || report).issues.primaryCategory}
                      </td>
                    </tr>
                    <tr>
                      <td>Emails Sent</td>
                      <td>
                        {isEditing ? (
                          <Form.Control
                            size="sm"
                            type="number"
                            value={(editableReport || report).communications?.emailsSent || 0}
                            onChange={(e) =>
                              setEditableReport((prev) => ({
                                ...prev,
                                communications: {
                                  ...prev.communications,
                                  emailsSent: Number(e.target.value)
                                }
                              }))
                            }
                          />
                        ) : (editableReport || report).communications?.emailsSent || 0}
                      </td>
                    </tr>
                  </tbody>
                </Table>
                {isEditing && (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="mb-2"
                    onClick={() =>
                      setEditableReport((prev) => ({
                        ...prev,
                        issues: {
                          ...prev.issues,
                          resolved: [...prev.issues.resolved, { title: '', status: '' }]
                        }
                      }))
                    }
                  >
                    Add Issue
                  </Button>
                )}
                <Table size="sm" bordered>
                  <thead>
                    <tr>
                      <th>Issue</th>
                      <th>Status</th>
                      {isEditing && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {(editableReport || report).issues.resolved.map((item, index) => (
                      <tr key={item.id || `${item.title}-${index}`}>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              value={item.title}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const resolved = [...prev.issues.resolved];
                                  resolved[index] = { ...resolved[index], title: e.target.value };
                                  return { ...prev, issues: { ...prev.issues, resolved } };
                                })
                              }
                            />
                          ) : item.title}
                        </td>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              value={item.status}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const resolved = [...prev.issues.resolved];
                                  resolved[index] = { ...resolved[index], status: e.target.value };
                                  return { ...prev, issues: { ...prev.issues, resolved } };
                                })
                              }
                            />
                          ) : item.status}
                        </td>
                        {isEditing && (
                          <td>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() =>
                                setEditableReport((prev) => {
                                  const resolved = prev.issues.resolved.filter((_, i) => i !== index);
                                  return { ...prev, issues: { ...prev.issues, resolved } };
                                })
                              }
                            >
                              Remove
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {(editableReport || report).issues.resolved.length === 0 && (
                      <tr>
                        <td colSpan={isEditing ? 3 : 2} className="text-muted text-center">No resolved queries recorded.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </>
            )}

            {sectionVisibility.communications && (editableReport || report).communications?.items?.length > 0 && (
              <Table size="sm" bordered>
                <thead>
                  <tr>
                    <th>Email Subject</th>
                    <th>Sent At</th>
                    {isEditing && <th />}
                  </tr>
                </thead>
                <tbody>
                  {(editableReport || report).communications.items.map((item, index) => (
                    <tr key={`${item.subject}-${index}`}>
                      <td>
                        {isEditing ? (
                          <Form.Control
                            size="sm"
                            value={item.subject}
                            onChange={(e) =>
                              setEditableReport((prev) => {
                                const items = [...prev.communications.items];
                                items[index] = { ...items[index], subject: e.target.value };
                                return { ...prev, communications: { ...prev.communications, items } };
                              })
                            }
                          />
                        ) : item.subject}
                      </td>
                      <td>
                        {isEditing ? (
                          <Form.Control
                            size="sm"
                            value={item.sentAt}
                            onChange={(e) =>
                              setEditableReport((prev) => {
                                const items = [...prev.communications.items];
                                items[index] = { ...items[index], sentAt: e.target.value };
                                return { ...prev, communications: { ...prev.communications, items } };
                              })
                            }
                          />
                        ) : item.sentAt}
                      </td>
                      {isEditing && (
                        <td>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() =>
                              setEditableReport((prev) => {
                                const items = prev.communications.items.filter((_, i) => i !== index);
                                return { ...prev, communications: { ...prev.communications, items } };
                              })
                            }
                          >
                            Remove
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {isEditing && sectionVisibility.communications && (editableReport || report).communications && (
              <Button
                size="sm"
                variant="outline-secondary"
                className="mb-3"
                onClick={() =>
                  setEditableReport((prev) => ({
                    ...prev,
                    communications: {
                      ...prev.communications,
                      items: [...(prev.communications.items || []), { subject: '', sentAt: '' }]
                    }
                  }))
                }
              >
                Add Email Sent
              </Button>
            )}

            {sectionVisibility.weeklyDocuments && (editableReport || report).weeklyDocuments && (
              <>
                <h6>Weekly Documents</h6>
                <Table size="sm" bordered>
                  <tbody>
                    <tr>
                      <td>Submitted</td>
                      <td>
                        {isEditing ? (
                          <Form.Control
                            size="sm"
                            type="number"
                            value={(editableReport || report).weeklyDocuments.submitted}
                            onChange={(e) =>
                              setEditableReport((prev) => ({
                                ...prev,
                                weeklyDocuments: {
                                  ...prev.weeklyDocuments,
                                  submitted: Number(e.target.value)
                                }
                              }))
                            }
                          />
                        ) : (editableReport || report).weeklyDocuments.submitted}
                      </td>
                    </tr>
                    <tr>
                      <td>Outstanding</td>
                      <td>
                        {isEditing ? (
                          <Form.Control
                            size="sm"
                            type="number"
                            value={(editableReport || report).weeklyDocuments.outstanding || 0}
                            onChange={(e) =>
                              setEditableReport((prev) => ({
                                ...prev,
                                weeklyDocuments: {
                                  ...prev.weeklyDocuments,
                                  outstanding: Number(e.target.value)
                                }
                              }))
                            }
                          />
                        ) : (editableReport || report).weeklyDocuments.outstanding || 0}
                      </td>
                    </tr>
                  </tbody>
                </Table>
                <Table size="sm" bordered>
                  <thead>
                    <tr>
                      <th>Submitted Learners</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        {isEditing ? (
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={((editableReport || report).weeklyDocuments.submittedLearners || []).join(', ')}
                            onChange={(e) =>
                              setEditableReport((prev) => ({
                                ...prev,
                                weeklyDocuments: {
                                  ...prev.weeklyDocuments,
                                  submittedLearners: e.target.value.split(',').map(v => v.trim()).filter(Boolean)
                                }
                              }))
                            }
                          />
                        ) : (((editableReport || report).weeklyDocuments.submittedLearners || []).join(', ') || 'None')}
                      </td>
                    </tr>
                  </tbody>
                </Table>
                <Table size="sm" bordered>
                  <thead>
                    <tr>
                      <th>Outstanding Learners</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        {isEditing ? (
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={((editableReport || report).weeklyDocuments.outstandingLearners || []).join(', ')}
                            onChange={(e) =>
                              setEditableReport((prev) => ({
                                ...prev,
                                weeklyDocuments: {
                                  ...prev.weeklyDocuments,
                                  outstandingLearners: e.target.value.split(',').map(v => v.trim()).filter(Boolean)
                                }
                              }))
                            }
                          />
                        ) : (((editableReport || report).weeklyDocuments.outstandingLearners || []).join(', ') || 'None')}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </>
            )}

            {sectionVisibility.facilitatorReports && (editableReport || report).facilitatorReports && (
              <>
                <h6>Facilitator Reports</h6>
                <Table size="sm" bordered>
                  <tbody>
                    <tr>
                      <td>Total Submitted</td>
                      <td>
                        {isEditing ? (
                          <Form.Control
                            size="sm"
                            type="number"
                            value={(editableReport || report).facilitatorReports.total}
                            onChange={(e) =>
                              setEditableReport((prev) => ({
                                ...prev,
                                facilitatorReports: {
                                  ...prev.facilitatorReports,
                                  total: Number(e.target.value)
                                }
                              }))
                            }
                          />
                        ) : (editableReport || report).facilitatorReports.total}
                      </td>
                    </tr>
                  </tbody>
                </Table>
                {isEditing && (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="mb-2"
                    onClick={() =>
                      setEditableReport((prev) => ({
                        ...prev,
                        facilitatorReports: {
                          ...prev.facilitatorReports,
                          byFacilitator: [
                            ...(prev.facilitatorReports.byFacilitator || []),
                            { name: '', count: 0 }
                          ]
                        }
                      }))
                    }
                  >
                    Add Facilitator
                  </Button>
                )}
                <Table size="sm" bordered>
                  <thead>
                    <tr>
                      <th>Facilitator</th>
                      <th>Reports</th>
                      {isEditing && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {((editableReport || report).facilitatorReports.byFacilitator || []).map((item, index) => (
                      <tr key={item.id || `${item.name}-${index}`}>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              value={item.name}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const byFacilitator = [...prev.facilitatorReports.byFacilitator];
                                  byFacilitator[index] = { ...byFacilitator[index], name: e.target.value };
                                  return { ...prev, facilitatorReports: { ...prev.facilitatorReports, byFacilitator } };
                                })
                              }
                            />
                          ) : item.name}
                        </td>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              type="number"
                              value={item.count}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const byFacilitator = [...prev.facilitatorReports.byFacilitator];
                                  byFacilitator[index] = { ...byFacilitator[index], count: Number(e.target.value) };
                                  return { ...prev, facilitatorReports: { ...prev.facilitatorReports, byFacilitator } };
                                })
                              }
                            />
                          ) : item.count}
                        </td>
                        {isEditing && (
                          <td>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() =>
                                setEditableReport((prev) => {
                                  const byFacilitator = prev.facilitatorReports.byFacilitator.filter((_, i) => i !== index);
                                  return { ...prev, facilitatorReports: { ...prev.facilitatorReports, byFacilitator } };
                                })
                              }
                            >
                              Remove
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {(!(editableReport || report).facilitatorReports.byFacilitator || (editableReport || report).facilitatorReports.byFacilitator.length === 0) && (
                      <tr>
                        <td colSpan={isEditing ? 3 : 2} className="text-muted text-center">No facilitator reports recorded.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                {(editableReport || report).facilitatorReports.items?.length > 0 && (
                  <>
                    {isEditing && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="mb-2"
                        onClick={() =>
                          setEditableReport((prev) => ({
                            ...prev,
                            facilitatorReports: {
                              ...prev.facilitatorReports,
                              items: [
                                ...(prev.facilitatorReports.items || []),
                                { cohortName: '', type: '', weekStart: '', weekEnd: '', weeklyHighs: '', weeklyLows: '', date: '', dailyComment: '' }
                              ]
                            }
                          }))
                        }
                      >
                        Add Report Note
                      </Button>
                    )}
                  <Table size="sm" bordered>
                    <thead>
                      <tr>
                        <th>Cohort</th>
                        <th>Type</th>
                        <th>Range</th>
                        <th>Notes</th>
                        {isEditing && <th />}
                      </tr>
                    </thead>
                    <tbody>
                      {(editableReport || report).facilitatorReports.items.map((item, index) => {
                        const range = item.type === 'weekly_highs_lows'
                          ? `${item.weekStart || ''} - ${item.weekEnd || ''}`
                          : (item.date || '');
                        const detail = item.type === 'weekly_highs_lows'
                          ? `Highs: ${item.weeklyHighs || ''} | Lows: ${item.weeklyLows || ''}`
                          : `Comment: ${item.dailyComment || ''}`;
                        return (
                          <tr key={`${item.cohortId || item.cohortName}-${index}`}>
                            <td>
                              {isEditing ? (
                                <Form.Control
                                  size="sm"
                                  value={item.cohortName}
                                  onChange={(e) =>
                                    setEditableReport((prev) => {
                                      const items = [...prev.facilitatorReports.items];
                                      items[index] = { ...items[index], cohortName: e.target.value };
                                      return { ...prev, facilitatorReports: { ...prev.facilitatorReports, items } };
                                    })
                                  }
                                />
                              ) : item.cohortName}
                            </td>
                            <td>
                              {isEditing ? (
                                <Form.Control
                                  size="sm"
                                  value={item.type}
                                  onChange={(e) =>
                                    setEditableReport((prev) => {
                                      const items = [...prev.facilitatorReports.items];
                                      items[index] = { ...items[index], type: e.target.value };
                                      return { ...prev, facilitatorReports: { ...prev.facilitatorReports, items } };
                                    })
                                  }
                                />
                              ) : item.type}
                            </td>
                            <td>
                              {isEditing ? (
                                <Form.Control
                                  size="sm"
                                  value={range}
                                  onChange={(e) =>
                                    setEditableReport((prev) => {
                                      const items = [...prev.facilitatorReports.items];
                                      if (items[index].type === 'weekly_highs_lows') {
                                        const [weekStart, weekEnd] = e.target.value.split('-').map(v => v.trim());
                                        items[index] = { ...items[index], weekStart, weekEnd };
                                      } else {
                                        items[index] = { ...items[index], date: e.target.value };
                                      }
                                      return { ...prev, facilitatorReports: { ...prev.facilitatorReports, items } };
                                    })
                                  }
                                />
                              ) : range}
                            </td>
                            <td>
                              {isEditing ? (
                                <Form.Control
                                  size="sm"
                                  value={detail}
                                  onChange={(e) =>
                                    setEditableReport((prev) => {
                                      const items = [...prev.facilitatorReports.items];
                                      if (items[index].type === 'weekly_highs_lows') {
                                        const [highs, lows] = e.target.value.split('|').map(v => v.replace('Highs:', '').replace('Lows:', '').trim());
                                        items[index] = { ...items[index], weeklyHighs: highs || '', weeklyLows: lows || '' };
                                      } else {
                                        items[index] = { ...items[index], dailyComment: e.target.value };
                                      }
                                      return { ...prev, facilitatorReports: { ...prev.facilitatorReports, items } };
                                    })
                                  }
                                />
                              ) : detail}
                            </td>
                            {isEditing && (
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() =>
                                    setEditableReport((prev) => {
                                      const items = prev.facilitatorReports.items.filter((_, i) => i !== index);
                                      return { ...prev, facilitatorReports: { ...prev.facilitatorReports, items } };
                                    })
                                  }
                                >
                                  Remove
                                </Button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                  </>
                )}
              </>
            )}

            {sectionVisibility.teamsMismatch && (editableReport || report).teamsMismatch && (
              <>
                <h6>Teams Attendance Mismatches</h6>
                {isEditing && (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="mb-2"
                    onClick={() =>
                      setEditableReport((prev) => ({
                        ...prev,
                        teamsMismatch: {
                          ...prev.teamsMismatch,
                          items: [
                            ...(prev.teamsMismatch.items || []),
                            { learnerName: '', email: '', sessionCohortName: '', learnerCohortName: '' }
                          ]
                        }
                      }))
                    }
                  >
                    Add Mismatch
                  </Button>
                )}
                <Table size="sm" bordered>
                  <thead>
                    <tr>
                      <th>Learner</th>
                      <th>Email</th>
                      <th>Session Cohort</th>
                      <th>Learner Cohort</th>
                      {isEditing && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {((editableReport || report).teamsMismatch.items || []).map((item, index) => (
                      <tr key={`${item.email}-${index}`}>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              value={item.learnerName}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const items = [...prev.teamsMismatch.items];
                                  items[index] = { ...items[index], learnerName: e.target.value };
                                  return { ...prev, teamsMismatch: { ...prev.teamsMismatch, items } };
                                })
                              }
                            />
                          ) : item.learnerName}
                        </td>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              value={item.email}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const items = [...prev.teamsMismatch.items];
                                  items[index] = { ...items[index], email: e.target.value };
                                  return { ...prev, teamsMismatch: { ...prev.teamsMismatch, items } };
                                })
                              }
                            />
                          ) : item.email}
                        </td>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              value={item.sessionCohortName}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const items = [...prev.teamsMismatch.items];
                                  items[index] = { ...items[index], sessionCohortName: e.target.value };
                                  return { ...prev, teamsMismatch: { ...prev.teamsMismatch, items } };
                                })
                              }
                            />
                          ) : item.sessionCohortName}
                        </td>
                        <td>
                          {isEditing ? (
                            <Form.Control
                              size="sm"
                              value={item.learnerCohortName}
                              onChange={(e) =>
                                setEditableReport((prev) => {
                                  const items = [...prev.teamsMismatch.items];
                                  items[index] = { ...items[index], learnerCohortName: e.target.value };
                                  return { ...prev, teamsMismatch: { ...prev.teamsMismatch, items } };
                                })
                              }
                            />
                          ) : item.learnerCohortName}
                        </td>
                        {isEditing && (
                          <td>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() =>
                                setEditableReport((prev) => {
                                  const items = prev.teamsMismatch.items.filter((_, i) => i !== index);
                                  return { ...prev, teamsMismatch: { ...prev.teamsMismatch, items } };
                                })
                              }
                            >
                              Remove
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {(!(editableReport || report).teamsMismatch.items || (editableReport || report).teamsMismatch.items.length === 0) && (
                      <tr>
                        <td colSpan={isEditing ? 5 : 4} className="text-muted text-center">No mismatches detected.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default QAReportCenter;
